import React, { useCallback, useMemo } from 'react';
import { OverlayTooltip } from '../../overlay';
import { NotificationTextAsyncProps } from './type';

const NotificationTextAsync: React.FC<NotificationTextAsyncProps> = ({
  notificationText = '',
  notification = null,
}) => {
  const importFileName = useMemo(
    () => notification?.metaData?.importFile?.name || '',
    [notification?.metaData?.importFile?.name],
  );
  const sequenceName = useMemo(() => notification?.sequenceName || '', [
    notification?.sequenceName,
  ]);
  const totalCountNumber = useMemo(
    () => notification?.metaData?.totalCount || 0,
    [notification?.metaData?.totalCount],
  );
  const completedCountNumber = useMemo(
    () => notification?.metaData?.completedCount || 0,
    [notification?.metaData?.completedCount],
  );
  const emailCreditsNumber = useMemo(
    () => notification?.metaData?.emailCredits || 0,
    [notification?.metaData?.emailCredits],
  );
  const phoneCreditsNumber = useMemo(
    () => notification?.metaData?.phoneCredits || 0,
    [notification?.metaData?.phoneCredits],
  );
  const totalCreditsNumber = useMemo(
    () => notification?.metaData?.totalCredits || 0,
    [notification?.metaData?.totalCredits],
  );
  const verifiedEmailLeadsNumber = useMemo(
    () => notification?.metaData?.verifiedEmailLeads || 0,
    [notification?.metaData?.verifiedEmailLeads],
  );
  const verifiedPhoneLeadsNumber = useMemo(
    () => notification?.metaData?.verifiedPhoneLeads || 0,
    [notification?.metaData?.verifiedPhoneLeads],
  );

  const logList = useMemo(() => {
    if (notificationText) {
      // eslint-disable-next-line no-useless-escape
      const regex = /([^\{\}]+|\{\{[a-zA-Z0-9_]+\}\})/g;

      // Use the regex to match and split the string
      const result = notificationText.match(regex);
      return result;
    }
    return null;
  }, [notificationText, notification?.metaData]);

  const getTextByVariableDetail = useCallback(
    (variable) => {
      switch (variable) {
        case '{{import_file_name}}':
          return {
            text: importFileName,
            displayName:
              importFileName?.length > 15
                ? `${importFileName?.slice(0, 15)}..`
                : importFileName,
          };
        case '{{sequence_name}}':
          return {
            text: sequenceName,
            displayName:
              sequenceName?.length > 25
                ? `${sequenceName?.slice(0, 25)}..`
                : sequenceName,
          };
        case '{{total_count}}':
          return {
            text: totalCountNumber,
            displayName: totalCountNumber.toString(),
          };
        case '{{completed_count}}':
          return {
            text: completedCountNumber,
            displayName: completedCountNumber.toString(),
          };
        case '{{email_credits}}':
          return {
            text: emailCreditsNumber,
            displayName: emailCreditsNumber.toString(),
          };
        case '{{phone_credits}}':
          return {
            text: phoneCreditsNumber,
            displayName: phoneCreditsNumber.toString(),
          };
        case '{{total_credits}}':
          return {
            text: totalCreditsNumber,
            displayName: totalCreditsNumber.toString(),
          };
        case '{{verified_email_leads}}':
          return {
            text: verifiedEmailLeadsNumber,
            displayName: verifiedEmailLeadsNumber.toString(),
          };
        case '{{verified_phone_leads}}':
          return {
            text: verifiedPhoneLeadsNumber,
            displayName: verifiedPhoneLeadsNumber.toString(),
          };
        default:
          return variable || '';
      }
    },
    [importFileName, sequenceName, totalCountNumber, completedCountNumber],
  );

  const renderTextWithNewlines = useCallback(
    (text: string, baseKey: string) => {
      if (!text.includes('\n')) {
        return text;
      }

      const parts = text.split('\n');
      const result: React.ReactNode[] = [];

      parts.forEach((part, partIndex) => {
        if (partIndex > 0) {
          result.push(<br key={`${baseKey}-br`} />);
        }
        if (part) {
          result.push(part);
        }
      });

      return result;
    },
    [],
  );

  if (logList?.length > 0) {
    return (
      <span className="notification-data">
        {logList.map((item, index) => {
          if (
            item === '{{total_count}}' ||
            item === '{{completed_count}}' ||
            item === '{{email_credits}}' ||
            item === '{{phone_credits}}' ||
            item === '{{total_credits}}' ||
            item === '{{verified_email_leads}}' ||
            item === '{{verified_phone_leads}}'
          ) {
            const countData = getTextByVariableDetail(item);
            return (
              <span
                className="notification-data-bold"
                key={`notification-data-${index + 1}`}
              >
                {countData?.displayName || 0}
              </span>
            );
          }
          const isVariable = item.includes('{{') && item.includes('}}');

          if (isVariable) {
            const variableData = getTextByVariableDetail(item);
            return (
              <OverlayTooltip
                text={variableData?.text}
                key={`notification-data-bold-${index + 1}`}
              >
                <span className="notification-data-bold">
                  {variableData?.displayName || ''}
                </span>
              </OverlayTooltip>
            );
          }

          return (
            <span
              className="notification-data"
              key={`'notification-data-${index + 1}`}
            >
              {renderTextWithNewlines(item, `notification-data-${index + 1}`)}
            </span>
          );
        })}
      </span>
    );
  }

  return <></>;
};

export default NotificationTextAsync;
